import * as React from "react";
import { cn } from "@/lib/utils";
import { SectionLabel } from "./GradientText";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";

interface Step {
  step: number;
  title: string;
  description: string;
}

interface HowItWorksProps {
  title: string;
  subtitle?: string;
  steps: Step[];
}

/**
 * HowItWorks (Neo-Brutalist Version)
 *
 * Step-by-step guide with:
 * - Bold step numbers in colored boxes
 * - Hard shadows and lift effects
 * - Entrance animations on scroll
 */
export function HowItWorks({ title, subtitle, steps }: HowItWorksProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 lg:py-32 bg-[#FFFBF0]"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-500 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel color="blue" className="mb-6">
            Process
          </SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4 font-['Epilogue'] text-[#1A1A1A]">
            {title}
          </h2>

          {subtitle && (
            <p className="text-[#666666] text-base lg:text-lg font-['Sora']">
              {subtitle}
            </p>
          )}
        </div>

        {/* Steps */}
        <div className="relative max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <SpotlightCard
                key={step.step}
                accentColor="blue"
                className={cn(
                  "group",
                  "transition-all duration-300 ease-out",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
                style={{
                  transitionDelay: isVisible ? `${(index + 1) * 150}ms` : "0ms",
                }}
              >
                <SpotlightCardContent className="h-full p-6">
                  {/* Step number - rounded square */}
                  <div className="mb-6">
                    <div
                      className={cn(
                        "relative w-16 h-16 rounded-2xl",
                        "bg-[#3F5EF4]",
                        "border-3 border-black",
                        "shadow-[4px_4px_0_0_#000000]",
                        "flex items-center justify-center",
                        "text-white font-black text-2xl font-['Epilogue']",
                        "transition-all duration-200",
                        "group-hover:-translate-x-1 group-hover:-translate-y-1",
                        "group-hover:shadow-[6px_6px_0_0_#000000]"
                      )}
                    >
                      {step.step}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-bold text-[#1A1A1A] mb-3 font-['Epilogue']">
                    {step.title}
                  </h3>
                  <p className="text-[#666666] text-sm leading-relaxed font-['Sora']">
                    {step.description}
                  </p>
                </SpotlightCardContent>
              </SpotlightCard>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
